import { useEffect, useRef, useState, type ReactNode } from "react";
import { Button, Callout } from "@promptgatewaysadmin/ui";
import { errorCopy, type CopyVars } from "../copy/errors.js";
import { PortalRequestError } from "../api/client.js";
import { isNotAnError, type PortalError } from "../api/errors.js";

/**
 * Loading, empty and error are THREE DISTINCT TREATMENTS. Collapsing any two is the most
 * common defect in this family, and on the invoice table it means telling a paying customer
 * they have never been billed.
 */

/**
 * A skeleton at the content's FINAL GEOMETRY. A skeleton whose height differs from its
 * content causes a layout shift, which on this portal means a mis-click on a billing
 * control.
 *
 * Slow-network ladder: nothing for 300ms (do not flash a skeleton for a fast response),
 * skeletons to 10s, then "Still loading…" + Retry — an indefinite skeleton is
 * indistinguishable from a hang.
 */
export function Skeleton({
  width = "100%",
  height = 20,
  onRetry,
}: {
  width?: string | number;
  height?: number;
  onRetry?: () => void;
}) {
  const [phase, setPhase] = useState<"quiet" | "shown" | "stalled">("quiet");
  useEffect(() => {
    const a = setTimeout(() => setPhase("shown"), 300);
    const b = setTimeout(() => setPhase("stalled"), 10_000);
    return () => {
      clearTimeout(a);
      clearTimeout(b);
    };
  }, []);

  if (phase === "quiet") return <span style={{ display: "block", height }} aria-hidden="true" />;
  if (phase === "stalled") {
    return (
      <span className="pg-stalled" role="status">
        Still loading…{" "}
        {onRetry && (
          <Button size="sm" variant="ghost" onClick={onRetry}>
            Retry
          </Button>
        )}
      </span>
    );
  }
  return (
    <span
      className="pg-skeleton"
      style={{ width, height }}
      aria-hidden="true"
      data-testid="skeleton"
    />
  );
}

/** Wraps a region whose contents are still arriving. Resolved content announces once. */
export function Loading({ busy, children }: { busy: boolean; children: ReactNode }) {
  return (
    <div aria-busy={busy || undefined} aria-live="polite">
      {children}
    </div>
  );
}

/**
 * Fail at the SMALLEST SCOPE THAT FAILED. One failed call must never blank a page: the
 * invoice table failing is a table-level error, and the shell, plan card and nav are
 * unaffected.
 *
 * Every error has an action, or says what happens next. Never a dead end.
 */
export function ErrorState({
  error,
  fallbackKey,
  vars,
  onRetry,
}: {
  error: unknown;
  fallbackKey: string;
  vars?: CopyVars;
  onRetry?: () => void;
}) {
  const portalError = toPortalError(error);
  const key = portalError?.copyKey ?? fallbackKey;
  const copy = errorCopy(key, vars);
  return (
    <div className="pg-errorstate" role="alert">
      <p className="pg-errorstate__body">{copy.body}</p>
      {onRetry && copy.action && (
        <Button size="sm" variant="secondary" onClick={onRetry}>
          {copy.action}
        </Button>
      )}
    </div>
  );
}

/** Banner-level failure, for page-wide conditions. `role="alert"` — it interrupts. */
export function ErrorBanner({
  error,
  fallbackKey,
  vars,
  onRetry,
}: {
  error: unknown;
  fallbackKey: string;
  vars?: CopyVars;
  onRetry?: () => void;
}) {
  const copy = errorCopy(toPortalError(error)?.copyKey ?? fallbackKey, vars);
  return (
    <Callout tone="danger" role="alert">
      {copy.body}
      {onRetry && copy.action ? (
        <>
          {" "}
          <Button size="sm" variant="ghost" onClick={onRetry}>
            {copy.action}
          </Button>
        </>
      ) : null}
    </Callout>
  );
}

export function toPortalError(error: unknown): PortalError | null {
  if (!(error instanceof PortalRequestError)) return null;
  return isNotAnError(error.failure) ? null : error.failure;
}

/**
 * The offline banner. Detected by navigator.onLine PLUS failed fetches — the API lies often
 * enough that both are needed.
 *
 * Cached data renders WITH the banner, never silently, and every mutating control is
 * disabled while it is up — especially the Stripe handoffs, or the click opens a blank tab.
 */
export function useOnline(): boolean {
  const [online, setOnline] = useState(() =>
    typeof navigator === "undefined" ? true : navigator.onLine !== false,
  );
  useEffect(() => {
    const up = () => setOnline(true);
    const down = () => setOnline(false);
    window.addEventListener("online", up);
    window.addEventListener("offline", down);
    return () => {
      window.removeEventListener("online", up);
      window.removeEventListener("offline", down);
    };
  }, []);
  return online;
}

export function OfflineBanner() {
  const online = useOnline();
  const wasOffline = useRef(false);
  const [announcement, setAnnouncement] = useState("");

  useEffect(() => {
    if (!online) wasOffline.current = true;
    else if (wasOffline.current) {
      wasOffline.current = false;
      setAnnouncement("Back online.");
    }
  }, [online]);

  return (
    <>
      {!online && (
        // role="status", not "alert": a persistent condition should be announced once
        // without interrupting whatever the user is reading.
        <Callout tone="warn" role="status">
          You&apos;re offline. Showing your last-loaded information.
        </Callout>
      )}
      <span className="pg-visually-hidden" role="status" aria-live="polite">
        {announcement}
      </span>
    </>
  );
}

/**
 * A control that is unavailable, WITH THE REASON REACHABLE.
 *
 * aria-disabled + aria-describedby rather than bare `disabled`: a disabled button is
 * removed from the tab order, which takes the explanation with it. "A disabled control with
 * no stated reason" is on the list of things that must never happen.
 */
export function DisabledWithReason({
  reason,
  children,
  id,
}: {
  reason: string;
  children: (props: { "aria-disabled": true; "aria-describedby": string }) => ReactNode;
  id: string;
}) {
  const reasonId = `${id}-reason`;
  return (
    <span className="pg-disabled-wrap">
      {children({ "aria-disabled": true, "aria-describedby": reasonId })}
      <span id={reasonId} className="pg-disabled-reason">
        {reason}
      </span>
    </span>
  );
}
